import { Injectable } from '@angular/core';
import {
  isConversation,
  OmniAnalyticsService,
  isAgentTimedOutConversation,
  isDMContextTags,
} from '@cigna/omni/shared';
import {
  ConversationDTO,
  ConversationEvent,
  ConversationMessageEventDTO,
  ConversationStateEventDTO,
  ConversationSystemMessageEvent,
} from '@cigna/vampire-dto';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  filter,
  map,
  tap,
  withLatestFrom,
  distinctUntilKeyChanged,
} from 'rxjs/operators';
import {
  ConversationDiff,
  ConversationsActionTypes,
  ConversationsFailureActions,
  CreateConversationSuccess,
  TransferConversationSuccess,
  MessageViewed,
  PostMessage,
  GetConversationSuccess,
  EndConversation,
} from './conversations.actions';
import { OmniConversationsFacade } from './conversations.facade';
import { Dictionary } from '@ngrx/entity';
import { FeatureTogglesFacade } from '@cigna/features';

export interface ConversationEvents extends ConversationEvent {
  changeType?: string;
}

export interface ConversationMetaExtens {
  ENGAGEMENT_ID: string;
  AGENT_IDENTIFIER: string;
}

export interface ConversationInterface extends ConversationDTO {
  events: ConversationEvents[];
  uswState?: string;
}
@Injectable()
export class ConversationsAnalyticsEffects {
  chatStartSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CreateConversationSuccess>(
          ConversationsActionTypes.CreateConversationSuccess,
        ),
        tap(({ payload: { context, _id, type } }) => {
          this.analytics.track('chatStartSuccess', {
            chatContext: context.tags,
            chatId: _id,
            chatType: type,
          });
        }),
      ),
    { dispatch: false },
  );

  chatTransfer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<TransferConversationSuccess>(
          ConversationsActionTypes.TransferConversationSuccess,
        ),
        tap(
          ({
            payload: {
              nextConversation: { context, _id, type },
            },
          }) => {
            this.analytics.track('chatTransfer', {
              chatContext: context.tags,
              chatId: _id,
              chatType: type,
            });
          },
        ),
      ),
    { dispatch: false },
  );

  agentToAgentTransfer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<GetConversationSuccess>(
          ConversationsActionTypes.GetConversationSuccess,
        ),
        filter(
          ({ payload }: { payload: ConversationInterface }) =>
            payload.uswState === 'inQueue' &&
            payload.events.filter((event) => event.type === 'participant').pop()
              ?.changeType === 'left',
        ),
        tap(() => {
          this.analytics.track('agentToAgentTransfer', {});
        }),
      ),
    { dispatch: false },
  );

  chatEnd$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ConversationDiff>(ConversationsActionTypes.ConversationDiff),
        filter(
          ({ payload: { newEvents } }) =>
            !!newEvents
              .filter((e): e is ConversationStateEventDTO => e.type === 'state')
              .find((e) => e.state === 'closed'),
        ),
        withLatestFrom(this.conversationsFacade.conversationsEntities$),
        map(([{ payload }, convs]) => convs[payload._id]),
        filter(
          (conv): conv is ConversationDTO =>
            isConversation(conv) && conv.type !== 'bot',
        ),
        tap((conv) => {
          // eslint-disable-next-line no-unused-expressions
          isAgentTimedOutConversation(conv.events)
            ? this.analytics.track('chatEndError', {
                chatContext: conv.context.tags,
                chatId: conv._id,
                chatType: conv.type,
                chatEndErrorReason: 'AGENT_TIMEOUT',
              })
            : this.analytics.track('chatEnd', {
                chatContext: conv.context.tags,
                chatId: conv._id,
                chatSurveyId:
                  conv.context.meta && conv.context.meta.ENGAGEMENT_ID
                    ? conv.context.meta.ENGAGEMENT_ID
                    : conv._id,
                chatType: conv.type,
                agentId: (conv.context.meta as ConversationMetaExtens)
                  ?.AGENT_IDENTIFIER,
              });
        }),
      ),
    { dispatch: false },
  );

  endConversation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<EndConversation>(ConversationsActionTypes.EndConversation),
        withLatestFrom(this.conversationsFacade.conversationsEntities$),
        map(([{ payload }, convs]) => convs[payload]),
        filter(
          (conv): conv is ConversationDTO =>
            isConversation(conv) &&
            conv.events.some((event) => event.type === 'wait'),
        ),
        tap((conv) => {
          this.analytics.track('chatEnd', {
            chatContext: conv.context.tags,
            chatId: conv._id,
            chatSurveyId:
              conv.context.meta && conv.context.meta.ENGAGEMENT_ID
                ? conv.context.meta.ENGAGEMENT_ID
                : conv._id,
            chatType: conv.type,
            agentId: (conv.context.meta as ConversationMetaExtens)
              ?.AGENT_IDENTIFIER,
          });
        }),
      ),
    { dispatch: false },
  );

  firstMessageSent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<PostMessage>(ConversationsActionTypes.PostMessage),
        withLatestFrom(
          this.conversationsFacade.conversationId$,
          this.conversationsFacade.conversationEntities$,
        ),
        filter((c): c is [PostMessage, string, Dictionary<ConversationDTO>] => {
          const conv = c[1] && c[2]?.[c[1]];
          return (
            !!conv && isConversation(conv) && isDMContextTags(conv.context.tags)
          );
        }),
        tap(([, id, entities]) => {
          const selectedConv = entities?.[id];
          if (selectedConv) {
            const customerId = selectedConv.participants.find((p) =>
              ['customer', 'chcp'].includes(p.role),
            )?._id;
            const custMessages = selectedConv.events
              .filter(
                (e): e is ConversationMessageEventDTO => e.type === 'message',
              )
              .filter((me) => me.participantId === customerId);

            if (custMessages.length === 0) {
              this.analytics.trackEvent('firstMessageSent', {
                chatId: selectedConv._id,
              });
            }
          }
        }),
      ),
    { dispatch: false },
  );

  messageViewed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<MessageViewed>(ConversationsActionTypes.MessageViewed),
        filter(({ payload }) => !!payload && payload !== 'noop'),
        distinctUntilKeyChanged('payload'),
        withLatestFrom(this.conversationsFacade.conversation$),
        filter(
          (c): c is [MessageViewed, ConversationDTO] =>
            !!c[1] && isDMContextTags(c[1].context.tags),
        ),
        tap(([action, conversation]) => {
          if (
            !conversation.participants.find((participant) =>
              ['customer', 'chcp'].includes(participant.role),
            )?.lastViewedEvent
          ) {
            this.analytics.trackEvent('firstMessageViewed', {
              chatId: conversation._id,
            });
          }
          if (
            conversation.events.find(
              (e): e is ConversationSystemMessageEvent =>
                e._id === action.payload,
            )?.type === 'systemMessage'
          ) {
            this.analytics.trackEvent('autoReplyDisplayed', {});
          }
        }),
      ),
    { dispatch: false },
  );

  conversationError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ConversationsFailureActions>(
          ConversationsActionTypes.CreateConversationFailure,
          ConversationsActionTypes.EndConversationFailure,
          ConversationsActionTypes.GetConversationFailure,
          ConversationsActionTypes.GetTransferredConversationFailure,
          ConversationsActionTypes.TransferConversationFailure,
          ConversationsActionTypes.LoadConversationsFailure,
          ConversationsActionTypes.MessageViewedFailure,
          ConversationsActionTypes.PostCtaFailure,
          ConversationsActionTypes.PostMessageFailure,
        ),
        withLatestFrom(
          this.featureToggleFacade.featuresEnabled(['isEvernorthEnabled']),
        ),
        tap(([action, isEvernorthEnabled]) => {
          const eventName = isEvernorthEnabled
            ? 'chatErrorDuringConv'
            : 'chatError';
          this.analytics.trackEvent(eventName, {
            serviceName: 'Conversation Service',
            error: action.error,
          });
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private analytics: OmniAnalyticsService,
    private conversationsFacade: OmniConversationsFacade,
    public actions$: Actions,
    private featureToggleFacade: FeatureTogglesFacade,
  ) {}
}
